import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import { Row } from "reactstrap"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import moment from "moment/moment"
import MiniCards from "../../components/Common/mini-card"
import MiniWidget from "../../components/Common/mini-widget"
import { buscarVendasDia, buscarVendasSemana } from "../../store/auth/profile/actions"
import { apiTotalizadorVendas } from "../../helpers/backend_helper"
import { buildParamsGrouper } from "../../helpers/data_helper"
import { Filters } from "../../common/data/definitions"
import { describeDiff, describeDiffColor, describeMoney, getCompaniesValuesByOptions } from "../../helpers/utils"
import {
  filtrosDoPeriodo,
  periodoAnterior,
  PROJECAO_TOTALIZADOR,
  resumirTotalizador
} from "./movimento-vendas-kpi-periodo"

const RESUMO_VAZIO = { vendas: 0, quantidade: 0, ticket: 0 }

const periodoDosFiltros = filtros => {
  const datas = (filtros || []).filter(filtro => filtro.id === "Data")
  const inicio = datas.find(filtro => filtro.condition === Filters.greaterThanOrEqualTo)
  const fim = datas.find(filtro => filtro.condition === Filters.lessThanOrEqualTo)
  return inicio && fim ? { inicio: inicio.value, fim: fim.value } : null
}

const totalizar = (filtros, periodo) =>
  apiTotalizadorVendas(buildParamsGrouper([], filtrosDoPeriodo(filtros, periodo), [], PROJECAO_TOTALIZADOR))
    .then(resumirTotalizador)

// Resposta de /vendas/dia e /vendas/semana: a primeira linha do resultado da consulta.
const primeiraLinha = resposta => (resposta && resposta[0] && resposta[0][0]) || {}

/**
 * Cards do Movimento de Vendas. Os do período somam exatamente as vendas da grade — mesma
 * empresa, período, situação e demais filtros — pelo mesmo totalizador (IW-348). Os de hoje
 * e da semana independem do período, mas respeitam a empresa selecionada.
 */
const MovimentoVendasKpiCards = props => {

  const { filters, companies, t } = props
  const [periodo, setPeriodo] = useState({ atual: RESUMO_VAZIO, anterior: RESUMO_VAZIO })
  const [carregandoPeriodo, setCarregandoPeriodo] = useState(false)
  const buscaAtual = useRef(0)

  const empresas = getCompaniesValuesByOptions(companies).join(",")

  useEffect(() => {
    if (!empresas) return
    props.buscarVendasDia({ data: moment().toDate(), companies: empresas, valorVenda: true, ticket: false })
    props.buscarVendasSemana({ data: moment().toDate(), companies: empresas, valorVenda: true, ticket: true })
  }, [empresas])

  // Os filtros são recriados a cada "Buscar": cada busca refaz os cards, e só a última
  // pode gravar o resultado, para uma resposta atrasada não sobrescrever a mais nova.
  useEffect(() => {
    const atual = periodoDosFiltros(filters)
    if (!atual) return

    const busca = ++buscaAtual.current
    setCarregandoPeriodo(true)

    Promise.all([totalizar(filters, atual), totalizar(filters, periodoAnterior(atual.inicio, atual.fim))])
      .then(([resumoAtual, resumoAnterior]) => {
        if (busca === buscaAtual.current) setPeriodo({ atual: resumoAtual, anterior: resumoAnterior })
      })
      .catch(() => {
        if (busca === buscaAtual.current) setPeriodo({ atual: RESUMO_VAZIO, anterior: RESUMO_VAZIO })
      })
      .finally(() => {
        if (busca === buscaAtual.current) setCarregandoPeriodo(false)
      })
  }, [filters])

  const dia = primeiraLinha(props.vendasDia)
  const semana = primeiraLinha(props.vendasSemana)
  const { atual, anterior } = periodo

  return (
    <React.Fragment>
      <Row>
        <MiniCards
          title={ t("movimentovendas.kpi.hoje.label") }
          tooltip={ t("movimentovendas.kpi.hoje.tooltip") }
          text={ describeMoney(dia.vendas || 0) }
          iconClass="bx-import"
          loading={ !!props.vendasDiaLoading }
        />

        <MiniCards
          title={ t("movimentovendas.kpi.semana.label") }
          tooltip={ t("movimentovendas.kpi.semana.tooltip") }
          text={ describeMoney(semana.vendas || 0) }
          iconClass="bx-copy-alt"
          loading={ !!props.vendasSemanaLoading }
        />

        <MiniCards
          title={ t("movimentovendas.kpi.ticketsemana.label") }
          tooltip={ t("movimentovendas.kpi.ticketsemana.tooltip") }
          text={ describeMoney(semana.ticket || 0) }
          iconClass="bx-purchase-tag-alt"
          loading={ !!props.vendasSemanaLoading }
        />
      </Row>

      <Row>
        <MiniWidget
          icon="bx bx-archive-in"
          title={ t("movimentovendas.edicao.vendasperiodo.label") }
          tooltip={ t("movimentovendas.kpi.vendasperiodo.tooltip") }
          value={ describeMoney(atual.vendas) }
          badgeValue={ describeDiff(atual.vendas, anterior.vendas) }
          color={ describeDiffColor(atual.vendas, anterior.vendas) }
          desc={ t("movimentovendas.edicao.vendasperiodo.anterior.label") }
          loading={ carregandoPeriodo }
        />

        <MiniCards
          title={ t("movimentovendas.edicao.vendasultimoperiodo.label") }
          tooltip={ t("movimentovendas.kpi.vendasultimoperiodo.tooltip") }
          text={ describeMoney(anterior.vendas) }
          iconClass="bx-archive-in"
          loading={ carregandoPeriodo }
        />

        <MiniWidget
          icon="bx bx-purchase-tag-alt"
          title={ t("movimentovendas.edicao.ticketperiodo.label") }
          tooltip={ t("movimentovendas.kpi.ticketperiodo.tooltip") }
          value={ describeMoney(atual.ticket) }
          badgeValue={ describeDiff(atual.ticket, anterior.ticket) }
          color={ describeDiffColor(atual.ticket, anterior.ticket) }
          desc={ t("movimentovendas.edicao.ticketperiodo.anterior.label") }
          loading={ carregandoPeriodo }
        />
      </Row>
    </React.Fragment>
  )
}

MovimentoVendasKpiCards.propTypes = {
  filters: PropTypes.array,
  companies: PropTypes.array,
  vendasDia: PropTypes.any,
  vendasDiaLoading: PropTypes.bool,
  vendasSemana: PropTypes.any,
  vendasSemanaLoading: PropTypes.bool,
  buscarVendasDia: PropTypes.func,
  buscarVendasSemana: PropTypes.func,
  t: PropTypes.func
}

const mapStatetoProps = state => {
  const { vendasDia, vendasDiaLoading, vendasSemana, vendasSemanaLoading } = state.Profile
  return { vendasDia, vendasDiaLoading, vendasSemana, vendasSemanaLoading }
}

export default withTranslation()(connect(mapStatetoProps, {
  buscarVendasDia,
  buscarVendasSemana
})(MovimentoVendasKpiCards))
